import { assertMover, isPassable } from './clearance.js';
import { vec2 } from './geometry.js';
import { cellAt, cellIndex, createGrid, inBounds, isWalkable, walkableNeighbours } from './grid.js';
/**
 * Tuning, not law. `wallChance` and `smoothingPasses` are still placeholders;
 * the size is not, and stopped being one on 2026-08-12.
 *
 * **128×128, because 64×64 played small.** Ronald walked the deployed floor and
 * said so, and the number that replaced it is the largest the tick can afford
 * rather than the largest that would feel good: a floor is ~41 bytes of seed at
 * any size, and generating one and measuring its clearance are both paid once —
 * but a chase pays a full-map Dijkstra sweep at 20 Hz (`TODO.md` #273), and that
 * one scales with the *area*. Measured on the deploy host: 64² sweeps in 16.5 ms,
 * 128² in 32.7, 256² in 141 and 512² in 405, against a 50 ms tick. So 256² is
 * two ticks of sweep and 512² is eight, and the size above this one is blocked
 * on a cheaper step order rather than on taste (`TODO.md` #350).
 *
 * What it buys: ~10,500 walkable cells against ~2,400 — a floor a party crosses
 * rather than one it fills.
 */
export const DEFAULT_DUNGEON_OPTIONS = {
    width: 128,
    height: 128,
    wallChance: 0.45,
    smoothingPasses: 4,
};
/** Below 3×3 there is no interior to dig, only border. */
const MIN_DIMENSION = 3;
/**
 * Rock wins a cell when it holds this many of the nine cells in the 3×3 block —
 * the cell itself included. The classic 4-5 rule.
 *
 * Counting the cell itself is what makes the automaton settle: leave it out and
 * every pass re-decides each cell from its surroundings alone, which erodes the
 * rock away into one big empty room within a few rounds.
 */
const CROWDING_THRESHOLD = 5;
/** Corridors run orthogonally: a one-cell diagonal corridor would be exactly the corner squeeze movement forbids. */
const ORTHOGONAL_STEPS = [
    { x: 0, y: -1 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: -1, y: 0 },
];
const assertDimension = (name, value) => {
    if (!Number.isInteger(value) || value < MIN_DIMENSION) {
        throw new RangeError(`Dungeon ${name} must be an integer of at least ${MIN_DIMENSION}, received ${value}`);
    }
};
const assertOptions = ({ width, height, wallChance, smoothingPasses }) => {
    assertDimension('width', width);
    assertDimension('height', height);
    if (!(wallChance >= 0 && wallChance <= 1)) {
        throw new RangeError(`Dungeon wallChance must be a probability in [0, 1], received ${wallChance}`);
    }
    if (!Number.isInteger(smoothingPasses) || smoothingPasses < 0) {
        throw new RangeError(`Dungeon smoothingPasses must be a non-negative integer, received ${smoothingPasses}`);
    }
};
const isOnRing = (grid, cell) => cell.x === 0 || cell.y === 0 || cell.x === grid.width - 1 || cell.y === grid.height - 1;
/** The map is a closed cave system: the outermost ring is always rock. */
const sealBorder = (grid) => createGrid(grid.width, grid.height, grid.cells.map((walkable, index) => walkable && !isOnRing(grid, cellAt(grid, index))));
const scatterRock = (rng, { width, height, wallChance }) => sealBorder(createGrid(width, height, Array.from({ length: width * height }, () => rng.next() >= wallChance)));
/**
 * One round of the automaton: a cell is rock when its neighbourhood is crowded
 * with rock, floor otherwise. Off-map counts as rock, which is what pulls the
 * cave away from the edges of the world.
 */
const smooth = (grid) => {
    const cells = [];
    for (let y = 0; y < grid.height; y += 1) {
        for (let x = 0; x < grid.width; x += 1) {
            let rock = 0;
            for (let dy = -1; dy <= 1; dy += 1) {
                for (let dx = -1; dx <= 1; dx += 1) {
                    if (!isWalkable(grid, vec2(x + dx, y + dy)))
                        rock += 1;
                }
            }
            cells.push(rock < CROWDING_THRESHOLD);
        }
    }
    return sealBorder(createGrid(grid.width, grid.height, cells));
};
/**
 * Labels each walkable cell with the id of the region it belongs to, walls with
 * `-1`. Regions are numbered in reading order.
 *
 * The flood fill uses the movement rule itself (`walkableNeighbours`), so a
 * region is precisely the set of cells an entity can actually walk between —
 * never a diagonal seam that only looks connected from above.
 */
const labelRegions = (grid) => {
    const labels = new Array(grid.cells.length).fill(-1);
    let count = 0;
    for (const [index, walkable] of grid.cells.entries()) {
        if (!walkable || labels[index] !== -1)
            continue;
        const label = count;
        count += 1;
        labels[index] = label;
        let frontier = [cellAt(grid, index)];
        while (frontier.length > 0) {
            const next = [];
            for (const cell of frontier) {
                for (const neighbour of walkableNeighbours(grid, cell)) {
                    const neighbourIndex = cellIndex(grid, neighbour);
                    if (labels[neighbourIndex] !== -1)
                        continue;
                    labels[neighbourIndex] = label;
                    next.push(neighbour);
                }
            }
            frontier = next;
        }
    }
    return { labels, count };
};
/** Orthogonal neighbours to tunnel into — rock included, the outer ring excluded. */
const tunnelNeighbours = (grid, index) => {
    const cell = cellAt(grid, index);
    return ORTHOGONAL_STEPS.flatMap((step) => {
        const candidate = vec2(cell.x + step.x, cell.y + step.y);
        if (isOnRing(grid, candidate))
            return [];
        const candidateIndex = cellIndex(grid, candidate);
        return candidateIndex >= 0 ? [candidateIndex] : [];
    });
};
/**
 * Joins every walkable region of a map into one, by tunnelling.
 *
 * A breadth-first wave spreads out from the first region *through rock*; the
 * first time it washes over a region it has not connected yet, the route it
 * arrived by is carved into a corridor. One sweep connects everything, and each
 * corridor is the shortest tunnel between that region and the connected set.
 *
 * Only ever adds floor — no cave is deleted to make the map connect — and never
 * touches the outer ring, so a corridor cannot lead off the map.
 */
export const connectRegions = (grid) => {
    const { labels, count } = labelRegions(grid);
    if (count <= 1)
        return grid;
    const cells = [...grid.cells];
    const cameFrom = new Array(cells.length).fill(-1);
    const reached = new Array(cells.length).fill(false);
    const connected = new Array(count).fill(false);
    connected[0] = true;
    let frontier = labels.flatMap((label, index) => label === 0 && !isOnRing(grid, cellAt(grid, index)) ? [index] : []);
    for (const index of frontier)
        reached[index] = true;
    while (frontier.length > 0) {
        const next = [];
        for (const current of frontier) {
            const label = labels[current] ?? -1;
            if (label >= 0 && connected[label] !== true) {
                connected[label] = true;
                for (let step = current; step >= 0; step = cameFrom[step] ?? -1)
                    cells[step] = true;
            }
            for (const neighbour of tunnelNeighbours(grid, current)) {
                if (reached[neighbour] === true)
                    continue;
                reached[neighbour] = true;
                cameFrom[neighbour] = current;
                next.push(neighbour);
            }
        }
        frontier = next;
    }
    return createGrid(grid.width, grid.height, cells);
};
/** Keeps only what the entrance can actually reach. The invariant, made true rather than assumed. */
const keepReachable = (grid, entrance) => {
    const { labels } = labelRegions(grid);
    const entranceLabel = labels[cellIndex(grid, entrance)] ?? -1;
    return createGrid(grid.width, grid.height, labels.map((label) => label === entranceLabel));
};
/**
 * Every cell that counts as *against a map edge*, in reading order.
 *
 * `docs/GAME-DESIGN.md`: *"Entrance sits against a map edge; the stairs down are
 * placed at maximum flow-field distance from it."* The second half is
 * `descent.ts`'s `placeStairs`, and it measures *from here* — so an entrance in
 * the middle of the cave halves the longest walk the rule exists to buy, and
 * this is the half that makes the guarantee true.
 *
 * ## What "against the edge" means when the edge is solid rock
 *
 * It cannot mean row 0, the last row, column 0 or the last column: `sealBorder`
 * makes the outermost ring rock on every map this module produces, so the
 * literal reading names cells nobody can stand on and would leave no entrance
 * at all. The honest reading is **the nearest walkable cell inward**: come in
 * from outside the map along one of the four axes and stop at the first floor
 * you touch. That cell is on the cave's own outer surface by construction —
 * there is a straight run of rock from it to the edge of the world — which is
 * what the design is actually asking for.
 *
 * It also means a candidate is not always *near* the border: where a whole
 * flank of the map is rock, the ray on that flank keeps going until it finds
 * cave, and names a cell well inside the map. Such a cell is still the cave's
 * surface — that is what makes a cave sitting three rings in enterable at all —
 * but it is the weakest reading of "against the edge" available, so it is
 * `pickEntrance` that decides when one may be drawn, not this list.
 *
 * ## A disc, not a point
 *
 * The same argument `placeStairs` makes: an entrance a body cannot stand on is a
 * floor nobody can enter, so a candidate must pass the mover's own erosion
 * (`isPassable`, and therefore the clearance field) rather than merely be
 * walkable. Leaving the mover out asks the question for a point, exactly as
 * every other entry point in core does.
 *
 * A ray stops at the **first walkable cell**, admissible or not — it never walks
 * on until the body fits. Walking on would answer with a cell deeper in the
 * cave, which is precisely the entrance-in-the-middle this rule exists to
 * refuse, and it would do it silently on exactly the maps where it matters. So
 * an inadmissible first cell removes that ray from the answer, and a map with no
 * admissible ray at all has no entrance for that body — see `pickEntrance`.
 *
 * Worth knowing before choosing a radius: **every candidate has a clearance of
 * exactly 0.5**, always. The first floor cell on a ray has rock (or the off-map
 * rock the clearance field counts as rock) immediately behind it, and a walkable
 * cell one step from rock is half a cell from it. So this filter is all-or-
 * nothing rather than map-dependent: a body of half a cell or less — which is
 * what `apps/game`'s `PLAYER_RADIUS` is — fits on every candidate on every map,
 * and anything wider fits on none of them. An edge entrance and a body wider
 * than half a cell are mutually exclusive, on any map, by geometry.
 *
 * ## Why the order cannot leak
 *
 * Four families of rays run here — down the columns, up them, along the rows,
 * back along them — and a corner cell is commonly the first floor on two of
 * them. Candidates are marked in an array **indexed by cell** and collected
 * row-major from it, so each is named exactly once and the list is sorted by
 * cell index whatever order the rays ran in. That is the same reasoning
 * `placeStairs` scans its `distances` array by, and for the same reason: an
 * answer that depended on the order a search happened to settle in is a desync
 * that shows up on one seed in fifty, on one player's client.
 */
export const entranceCandidates = (grid, mover) => {
    assertMover(grid, mover);
    const marked = new Array(grid.cells.length).fill(false);
    const scanInward = (start, step) => {
        for (let cell = start; inBounds(grid, cell); cell = vec2(cell.x + step.x, cell.y + step.y)) {
            if (!isWalkable(grid, cell))
                continue;
            if (isPassable(grid, cell, mover))
                marked[cellIndex(grid, cell)] = true;
            return;
        }
    };
    for (let x = 0; x < grid.width; x += 1) {
        scanInward(vec2(x, 0), vec2(0, 1));
        scanInward(vec2(x, grid.height - 1), vec2(0, -1));
    }
    for (let y = 0; y < grid.height; y += 1) {
        scanInward(vec2(0, y), vec2(1, 0));
        scanInward(vec2(grid.width - 1, y), vec2(-1, 0));
    }
    return marked.flatMap((hit, index) => (hit ? [cellAt(grid, index)] : []));
};
/** How far in from the nearest border of the map a cell sits. `0` on the outermost ring. */
const insetFromBorder = (grid, cell) => Math.min(cell.x, cell.y, grid.width - 1 - cell.x, grid.height - 1 - cell.y);
/**
 * How far in from the border the way in aims to sit, in cells.
 *
 * **Three, and it is a measured number rather than a taste.** The rule this
 * replaced took the *shallowest* candidates, which put every spawn exactly one
 * cell from the border — 8 of 8 seeds at 128×128 — and a character standing
 * there has a wedge of the isometric view hanging off the map, where a click
 * lands on nothing. That is an input dead zone at the one cell every run starts
 * on, so the draw moves inward.
 *
 * How far inward is bounded from the other side by the walk. `placeStairs`
 * measures from here, so every cell of margin is a cell the traversal may lose,
 * and the bound `docs/GAME-DESIGN.md` claims — a longest walk over
 * `(width + height) / 2` — is only about 7% clear at 48×48. Measured over 600
 * seeds at that size, as the shortest walk any draw from the shortlist could
 * leave: 50.0 against a bound of 48 at the rim, 48.1 at two cells in, 47.6 at
 * three, 47.0 at four and 45.2 at five, and the number of seeds where *some*
 * draw falls under the bound is 0, 0, 2, 9 and 17 respectively. Three is where
 * that curve is still flat, four is where it turns. At the size the game is
 * actually played the choice is free — 128×128 has 151 of walk against a bound
 * of 128 at three cells in, and would carry eight — so this constant is set by
 * the smallest map the suite sweeps, not by the played one.
 */
const ENTRANCE_MARGIN = 3;
/** How far a distance from the border misses `ENTRANCE_MARGIN`, in either direction. */
const missesMargin = (inset) => Math.abs(inset - ENTRANCE_MARGIN);
/**
 * The distance from the border the draw will use: the one nearest the aim, and
 * the deeper of two that miss it equally.
 *
 * Nearest rather than "at least three in", because a cave whose only deep
 * surface is sixteen cells inside a forty-eight-cell map would answer that with
 * its middle — the entrance the shortlist exists to refuse. Where the aim is
 * unreachable this degrades towards the border the rule came from: a cave that
 * hugs its own rim, or a map too small to have a third ring, opens on the
 * deepest cell it actually has rather than refusing.
 *
 * The tie goes inward on the same argument the margin is chosen by: erring deep
 * costs a little of the walk, erring shallow costs the view this rule exists to
 * buy back.
 */
const drawnMargin = (grid, candidates) => candidates.reduce((best, cell) => {
    const inset = insetFromBorder(grid, cell);
    // The seed misses by infinity, so the first candidate always takes it and no
    // real distance is ever compared against a cell that does not exist.
    return missesMargin(inset) < missesMargin(best) || (missesMargin(inset) === missesMargin(best) && inset > best)
        ? inset
        : best;
}, Number.NEGATIVE_INFINITY);
/**
 * Draws the way in: one cell on the cave's surface, a few cells in from the
 * border, from the seed and nothing else.
 *
 * **Among the candidates, the ones a set distance in.** A ray that had to
 * travel to find cave still names the cave's own surface — that is what makes a
 * cave sitting three rings inside the map enterable at all — but a cell sixteen
 * cells into a forty-eight-cell map is one a player would call the middle of the
 * cave, and it costs the walk: measured over 200 seeds at 48×48, drawing from
 * every candidate leaves floors whose longest walk is 46 cells against a floor
 * 48 wide. So the shortlist is every candidate at **one** distance from the
 * border — `drawnMargin`'s — which is what keeps the walk while the draw itself
 * stays wide: 34 cells at 48×48 and 102 at the played 128×128, in the median.
 *
 * Which distance, and why it is not the rim, is `ENTRANCE_MARGIN`.
 *
 * Then **one draw** from the injected stream. Every step before it is a function
 * of the map alone: the distances are integer arithmetic on a cell, and the
 * shortlist keeps the candidate list's reading order, so the same seed opens the
 * same cave in the same place on the server and on every client, and no part of
 * the answer comes from the order anything was searched in.
 *
 * `null` is the one refusal, and it is a refusal rather than a fallback: no cell
 * against the edge admits this body (or the map has no floor at all). Answering
 * with the nearest walkable cell, or the roomiest one, or the middle, would make
 * this whole rule a no-op on exactly the maps it exists for. An ordinary
 * returned outcome, like `placeStairs`' own `null` — the caller decides whether
 * that means a narrower body or another seed. A mover built for a different map,
 * or a nonsense radius, *is* a broken program and throws.
 */
export const pickEntrance = (rng, grid, mover) => {
    const candidates = entranceCandidates(grid, mover);
    if (candidates.length === 0)
        return null;
    const margin = drawnMargin(grid, candidates);
    const shortlist = candidates.filter((cell) => insetFromBorder(grid, cell) === margin);
    // `rng.int` draws inside the list, so the fallback is unreachable —
    // `noUncheckedIndexedAccess` types the read as optional regardless.
    return shortlist[rng.int(0, shortlist.length)] ?? null;
};
/**
 * Grows a cave from a seed. The `Rng` is the only source of randomness, so the
 * same stream always yields the same dungeon.
 *
 * The entrance is drawn against a map edge (`pickEntrance`), for a point rather
 * than for a body — deliberately. A radius here would be a number the *client*
 * has to know: it would move the entrance, and the entrance is what
 * `keepReachable` prunes to and what a client places its player on, so a server
 * and a client disagreeing about it is a desync in the one thing a seed is
 * supposed to make free. Core states no body size at all (`TODO.md` #71), and
 * `apps/game`'s `PLAYER_RADIUS` is half a cell — which every cell against the
 * edge admits, so for the body the game actually walks the point answer and the
 * disc answer are the same cell. A caller with a wider body asks
 * `pickEntrance` itself and is told `null` rather than being quietly moved
 * inward.
 *
 * The automaton can, legitimately, fill the entire map with rock — at a high
 * `wallChance` that is simply what the dice said. There is then no cave to enter
 * and no edge to enter it by, so rather than fail, one cell is dug out at the
 * centre: a dungeon always has somewhere to stand, and that one cell is against
 * the edge by the same reading as any other — every ray that reaches it meets it
 * first.
 */
export const generateDungeon = (rng, options = {}) => {
    const settings = { ...DEFAULT_DUNGEON_OPTIONS, ...options };
    assertOptions(settings);
    let grid = scatterRock(rng, settings);
    for (let pass = 0; pass < settings.smoothingPasses; pass += 1)
        grid = smooth(grid);
    const connected = connectRegions(grid);
    const entrance = pickEntrance(rng, connected);
    if (entrance === null) {
        // Nothing walkable anywhere: no ray found floor, which for a point mover is
        // the only way the candidate list comes out empty.
        const centre = vec2(Math.floor(settings.width / 2), Math.floor(settings.height / 2));
        return {
            grid: createGrid(settings.width, settings.height, connected.cells.map((_walkable, index) => index === cellIndex(connected, centre))),
            entrance: centre,
        };
    }
    return { grid: keepReachable(connected, entrance), entrance };
};
//# sourceMappingURL=dungeon.js.map